import { FC, useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@kizen/kds/Typography';
import { Icon } from '@kizen/kds/Icon';
import { OutlinedTextarea as TextArea } from '../InputAreaWithPills';
import { toastVariant, useToast } from '__components/ToastProvider';
import { MessageHistory } from './MessageHistory';
import { AutoResponses } from './AutoResponses';
import { Controls } from './Controls';
import { ChatWrapper, MessageInput, Form, Error } from './styles';
import { v4 } from 'uuid';
import { KZN_FRAME_EVENTS } from '__pages/BusinessBuilderPublic/events';

export type MessageType = {
  id: string;
  isUserMessage: boolean;
  isResponseLoading: boolean;
  isResponseRefining?: boolean;
  isCollapsable: boolean;
  messageContent: string;
  header?: string;
};

type ChatProps = {
  isMobile?: boolean;
  fullScreen: boolean;
  inModal?: boolean;
  additonalSidePadding?: string;
  topPadding?: string;
  messages: MessageType[];
  autoResponses: string[];
  alert: string;
  errorMessage?: string;
  onSendMessage: (message: string) => void;
  onCreateNewChat: () => void;
  isLoadingNewSession: boolean;
  isMessageResponseLoading: boolean;
  isCompleteSessionLoading: boolean;
  showCompleteSessionButton?: boolean;
  showSkipQuestionsButton?: boolean;
  showAskMoreQuestionsButton?: boolean;
  disableCompeteSessionButton?: boolean;
  disableSkipQuestionsButton?: boolean;
  onCompleteSession: () => void;
  onAlert: () => void;
  placeholder: string;
  entityName?: string;
  trackEvent?: (event: string, payload?: any) => void;
};

export const chatDefaultPadding = 'padding: 0 200px 0 200px;';

export const mobileChatDefaultPadding = 'padding: 0 10px 0 10px;';

export const Chat: FC<ChatProps> = ({
  isMobile = false,
  fullScreen = false,
  inModal = false,
  additonalSidePadding,
  topPadding,
  messages = [],
  autoResponses = [],
  alert = '',
  errorMessage,
  onSendMessage,
  onCreateNewChat,
  isLoadingNewSession,
  isMessageResponseLoading,
  isCompleteSessionLoading,
  showCompleteSessionButton = false,
  showSkipQuestionsButton = false,
  showAskMoreQuestionsButton = false,
  disableCompeteSessionButton = false,
  disableSkipQuestionsButton = false,
  onCompleteSession,
  onAlert,
  placeholder,
  entityName,
  trackEvent,
}) => {
  const { t } = useTranslation();
  const [newMessage, setNewMessage] = useState<string>('');
  const [autoResponsePills, setAutoResponsePills] = useState<
    { id: string; value: string }[]
  >([]);

  const messageHistoryWrapperRef = useRef<HTMLDivElement>(null);
  const displayAlertToastRef = useRef<boolean>(true);
  const [focusTextArea, setFocusTextArea] = useState(false);

  const [showToast] = useToast();

  // focus text area
  useEffect(() => {
    if (focusTextArea) {
      document.getElementById('message-input')?.focus();
      setFocusTextArea(false);
    }
  }, [focusTextArea]);

  useEffect(() => {
    if (displayAlertToastRef.current && alert) {
      trackEvent?.(KZN_FRAME_EVENTS.SESSION_FLAGGED);
      displayAlertToastRef.current = false;
      showToast({
        message: `${t('Your request has been flagged for not following CoBuilder guidelines')}`,
        variant: toastVariant.FAILURE,
      });
      onAlert();
    }
  }, [alert, showToast, onAlert, t, trackEvent]);

  const handleAutoResponseSelect = useCallback((value: string) => {
    setAutoResponsePills((prev) => [...prev, { id: v4(), value: value }]);
    setFocusTextArea(true);
  }, []);

  const handleRemoveSelectAutoResponse = useCallback((id: string) => {
    setAutoResponsePills((prev) => prev.filter((pill) => pill.id !== id));
  }, []);

  const handleSendMessage = useCallback(
    (value: string, isCustomMessage: boolean = true) => {
      if (autoResponsePills.length > 0 && !isCustomMessage) {
        const message = autoResponsePills
          .map((pill) => pill.value)
          .reduce((acc, curr, index, array) => {
            if (index === 0 && array.length === 1) {
              return curr;
            }
            if (index === 0) {
              return `"${curr}"`;
            } else if (index === array.length - 1) {
              return `${acc} and "${curr}"`;
            } else {
              return `${acc}, "${curr}"`;
            }
          }, '');
        onSendMessage(message);
      } else {
        onSendMessage(value);
      }
      setAutoResponsePills([]);
    },
    [autoResponsePills, onSendMessage]
  );

  const chatSidePadding = useMemo(() => {
    if (isMobile && additonalSidePadding && fullScreen) {
      return `padding-right: ${additonalSidePadding}px; padding-left: ${additonalSidePadding}px;`;
    } else if (isMobile && fullScreen) {
      return 'padding-right: 0; padding-left: 0';
    } else if (isMobile) {
      return mobileChatDefaultPadding;
    } else if (additonalSidePadding && fullScreen) {
      return `padding-right: ${additonalSidePadding}px; padding-left: ${additonalSidePadding}px;`;
    } else if (fullScreen || inModal) {
      return 'padding-right: 0; padding-left: 0;';
    }
    return chatDefaultPadding;
  }, [additonalSidePadding, fullScreen, isMobile, inModal]);

  return (
    <ChatWrapper fullScreen={fullScreen} inModal={inModal}>
      {errorMessage ? (
        <Error>
          <Icon icon="message-info-circle" color="viz-seq-red-3" size="lg" />
          <Typography color="viz-seq-red-3" size="lg">
            {errorMessage}
          </Typography>
        </Error>
      ) : null}
      <MessageHistory
        fullScreen={fullScreen}
        sidePadding={chatSidePadding}
        topPadding={topPadding || '0'}
        isMobile={isMobile}
        inModal={inModal}
        messages={messages}
        autoResponses={autoResponses}
        messageHistoryWrapperRef={messageHistoryWrapperRef}
        isLoadingNewSession={isLoadingNewSession}
        isMessageResponseLoading={isMessageResponseLoading}
        shrinkHeightAmount={isMobile && showSkipQuestionsButton ? 50 : 0}
      />
      <Form>
        <AutoResponses
          isMobile={isMobile}
          isMessageResponseLoading={isMessageResponseLoading}
          autoResponses={autoResponses}
          onSelect={handleAutoResponseSelect}
          messageHistoryWrapperRef={messageHistoryWrapperRef}
          setFocusTextArea={setFocusTextArea}
          selectedResponses={autoResponsePills}
          chatSidePadding={chatSidePadding}
        />
        <MessageInput chatPadding={chatSidePadding}>
          <TextArea
            id="message-input"
            label=""
            placeholder={placeholder}
            value={newMessage}
            onChange={setNewMessage}
            disabled={Boolean(alert) || isCompleteSessionLoading}
            resize="none"
            rows={autoResponsePills.length > 0 ? 4 : 3}
            onRemovePill={handleRemoveSelectAutoResponse}
            pills={autoResponsePills}
          />
        </MessageInput>
        <Controls
          trackEvent={trackEvent}
          entityName={entityName}
          chatPadding={chatSidePadding}
          isMobile={isMobile}
          setNewMessage={setNewMessage}
          onSendMessage={handleSendMessage}
          onCreateNewChat={() => {
            displayAlertToastRef.current = true;
            setAutoResponsePills([]);
            onCreateNewChat();
          }}
          setDisplayAlertToast={() => (displayAlertToastRef.current = true)}
          newMessage={newMessage}
          onCompleteSession={() => {
            setAutoResponsePills([]);
            onCompleteSession();
          }}
          showBuildApplicationButton={showCompleteSessionButton}
          showSkipQuestionsButton={showSkipQuestionsButton}
          showAskMoreQuestionsButton={showAskMoreQuestionsButton}
          showCreateNewChatButton={messages.length > 1}
          disableBuildApplicationButton={
            isCompleteSessionLoading || disableCompeteSessionButton
          }
          disableSkipQuestionsButton={
            Boolean(alert) ||
            isLoadingNewSession ||
            isMessageResponseLoading ||
            isCompleteSessionLoading ||
            disableSkipQuestionsButton
          }
          disableCreateNewChatButton={isLoadingNewSession}
          disableAskMoreQuestionsButton={
            Boolean(alert) ||
            isLoadingNewSession ||
            isMessageResponseLoading ||
            isCompleteSessionLoading
          }
          disableSendMessageButton={
            Boolean(alert) ||
            isLoadingNewSession ||
            isMessageResponseLoading ||
            isCompleteSessionLoading ||
            (newMessage.trim() === '' && autoResponsePills.length === 0)
          }
          scrollToBottom={() =>
            messageHistoryWrapperRef.current?.scrollTo({
              top: messageHistoryWrapperRef.current.scrollHeight,
              behavior: 'smooth',
            })
          }
        />
      </Form>
    </ChatWrapper>
  );
};
